import React, { useRef } from 'react'
import { motion, useInView } from 'framer-motion'
import images from '../../app/utlis/image'
import BitEuProjectContent from '../organisms/ProjectsList/BitEuProject/BitEuProjectContent'
import BitEuProjectOverview from '../organisms/ProjectsList/BitEuProject/BitEuProjectOverview'
import AppsTestimonials from '../molecules/AppsTestimonials/AppsTestimonials'
import CompanyLogoImage from '../molecules/CompanyLogoImage/CompanyLogoImage'
import ContactHelp from '../molecules/ContactHelp/ContactHelp'
import Footer from '../organisms/Footer/Footer'
import { getPageData, useData } from '../../app/helper/functions'
import BLUECOIN from '../../app/assets/images/projectImage/bitEuImages/coinBlue.png'
import GREYCOIN from '../../app/assets/images/projectImage/bitEuImages/coinGrey.png'
import YELLOWCOIN from '../../app/assets/images/projectImage/bitEuImages/coinYellow.png'


const BitEuPage = () => {
    const { data } = useData();
    const bitEuProject = getPageData(data, 'bitEuAppPage');

    // Animation variants
    const fadeIn = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { duration: 0.8 } }
    }

    const slideInFromLeft = {
        hidden: { x: -50, opacity: 0 },
        visible: { x: 0, opacity: 1, transition: { duration: 0.8, ease: "easeOut" } }
    }

    const slideInFromRight = {
        hidden: { x: 50, opacity: 0 },
        visible: { x: 0, opacity: 1, transition: { duration: 0.8, ease: "easeOut" } }
    }

    const slideUp = {
        hidden: { y: 50, opacity: 0 },
        visible: { y: 0, opacity: 1, transition: { duration: 0.8, ease: "easeOut" } }
    }

    // Flip animations for the coins with different timings and looping with pause
    const flipGreyCoin = {
        animate: {
            rotateY: [0, 180, 360, 360, 360],
            rotate: [0, 15, 0, 0, 0],
            scale: [1, 1.1, 1, 1, 1],
            y: [-20, -10, 0, 0, -20],
            transition: {
                duration: 5,
                ease: "easeInOut",
                times: [0, 0.3, 0.6, 1, 1],
                repeat: Infinity,
                repeatDelay: 2
            }
        }
    }

    const flipBlueCoin = {
        animate: {
            rotateY: [0, 180, 360, 360, 360],
            rotate: [0, -15, 0, 0, 0],
            scale: [1, 1.15, 1, 1, 1],
            y: [-20, -5, 0, 0, -20],
            transition: {
                duration: 5,
                ease: "easeInOut",
                delay: 0.2,
                times: [0, 0.3, 0.6, 1, 1],
                repeat: Infinity,
                repeatDelay: 2
            }
        }
    }

    const flipYellowCoin = {
        animate: {
            rotateY: [0, 180, 360, 360, 360],
            rotate: [0, 20, 0, 0, 0],
            scale: [1, 1.2, 1, 1, 1],
            y: [-20, -8, 0, 0, -20],
            transition: {
                duration: 5,
                ease: "easeInOut",
                delay: 0.4,
                times: [0, 0.3, 0.6, 1, 1],
                repeat: Infinity,
                repeatDelay: 2
            }
        }
    }

    // Create refs for scroll animations
    const projectContentRef = useRef(null);
    const projectOverviewRef = useRef(null);
    const testimonialsRef = useRef(null);
    const companyLogoRef = useRef(null);
    const contactHelpRef = useRef(null);

    // Use useInView hook for each section
    const projectContentInView = useInView(projectContentRef, { once: false, amount: 0.3 });
    const projectOverviewInView = useInView(projectOverviewRef, { once: false, amount: 0.1 });
    const testimonialsInView = useInView(testimonialsRef, { once: false, amount: 0.3 });
    const companyLogoInView = useInView(companyLogoRef, { once: false, amount: 0.3 });
    const contactHelpInView = useInView(contactHelpRef, { once: false, amount: 0.3 });

    return (
        <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeIn}
            className="w-full overflow-x-hidden"
        >
            <div className='bg-bitEu-bg bg-center bg-cover bg-no-repeat'>
                <div className='bg-gradient-to-b from-[#DCCB63] to-[rgba(220,203,99,0)] '>
                    <div className='bg-blackTransparent'>
                        <div className='flex flex-col justify-center items-center gap-4 px-4 sm:px-8 md:px-16 pt-24 sm:pt-36 md:pt-52 pb-6 md:pb-10'>
                            <motion.div
                                className='flex justify-center items-center p-4 sm:p-8 md:p-12'
                                variants={slideUp}
                            >
                                <div className='relative max-w-[1000px]'>
                                    <motion.div
                                        className='w-[20%] h-[20%] flex items-center justify-center z-20 absolute top-[2%] left-[3%]'
                                        animate="animate"
                                        variants={flipGreyCoin}
                                        style={{ perspective: 1000 }}
                                    >
                                        <img src={GREYCOIN} alt="Grey coin" className="w-full h-auto" />
                                    </motion.div>
                                    <motion.img
                                        src={images.BITUEUBANNERIMAGE}
                                        className='w-full h-auto'
                                        alt="Bit.EU banner"
                                        whileHover={{ scale: 1.02 }}
                                        transition={{ type: "spring", stiffness: 300 }}
                                    />
                                    <motion.div
                                        className='w-[20%] h-[20%] flex items-center justify-center z-20 absolute -top-[2%] right-[1%]'
                                        animate="animate"
                                        variants={flipBlueCoin}
                                        style={{ perspective: 1000 }}
                                    >
                                        <img src={BLUECOIN} alt="Blue coin" className="w-full h-auto" />
                                    </motion.div>
                                    <motion.div
                                        className='w-[20%] h-[20%] flex items-center justify-center z-20 absolute bottom-[2%] right-[61%]'
                                        animate="animate"
                                        variants={flipYellowCoin}
                                        style={{ perspective: 1000 }}
                                    >
                                        <img src={YELLOWCOIN} alt="Yellow coin" className="w-full h-auto" />
                                    </motion.div>
                                </div>
                            </motion.div>

                            <motion.div
                                className='text-white flex flex-col gap-2 text-center w-full px-4 sm:pl-[5%] md:pl-[9%]'
                                variants={slideInFromLeft}
                            >
                                <motion.div
                                    className='capitalize font-bold text-4xl sm:text-5xl md:text-6xl'
                                    whileHover={{ scale: 1.02 }}
                                    transition={{ type: "spring", stiffness: 300 }}
                                >
                                    Bit.EU
                                </motion.div>
                                <motion.div
                                    className='font-medium text-lg sm:text-xl md:text-2xl'
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 0.2, duration: 0.6 }}
                                >
                                    Optimizing patient care: How we developed a website
                                    <br className="hidden sm:block" /> for excellent care clinics
                                </motion.div>
                            </motion.div>
                        </div>
                    </div>
                </div>
                <div className='bg-[#151517]'>
                    <motion.div
                        ref={projectContentRef}
                        initial="hidden"
                        animate={projectContentInView ? "visible" : "hidden"}
                        variants={slideUp}
                    >
                        <BitEuProjectContent bitEuProject={bitEuProject} />
                    </motion.div>
                </div>
            </div>

            <div className='bg-[#151517]'>
                <motion.div
                    ref={projectOverviewRef}
                    initial="hidden"
                    animate={projectOverviewInView ? "visible" : "hidden"}
                    variants={slideUp}
                >
                    <BitEuProjectOverview bitEuProject={bitEuProject} />
                </motion.div>
                <div className='mt-24 sm:mt-36 md:mt-56'>
                    <motion.div
                        className='flex items-center justify-center my-8'
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: false, amount: 0.3 }}
                    >
                        <button
                            onClick={() => {
                                window.open('https://play.google.com/store/apps/details?id=com.combiteu&pcampaignid=web_share', '_blank');
                            }}
                            className='bg-[#1A6FE6] text-white font-medium rounded-md px-6 py-3 md:px-8 md:py-4 text-lg hover:opacity-90 transition'
                            aria-label='View on Google Play'
                        >
                            View on Google Play →
                        </button>
                    </motion.div>
                    <motion.div
                        ref={testimonialsRef}
                        initial="hidden"
                        animate={testimonialsInView ? "visible" : "hidden"}
                        variants={slideUp}
                    >
                        <AppsTestimonials color='#DCCB63' bgcolor='bg-biteuColor' textColor='black' activeTextColor='white' />
                    </motion.div>
                    <motion.div
                        ref={companyLogoRef}
                        initial="hidden"
                        animate={companyLogoInView ? "visible" : "hidden"}
                        variants={slideInFromRight}
                        className='p-4 sm:p-6 md:p-10 px-4 sm:px-16 md:px-36'
                    >
                        <CompanyLogoImage />
                    </motion.div>
                    <motion.div
                        ref={contactHelpRef}
                        initial="hidden"
                        animate={contactHelpInView ? "visible" : "hidden"}
                        variants={slideUp}
                    >
                        <ContactHelp title="Let's connect & achieve your business goals together" buttonTitle='CONTACT US' />
                    </motion.div>
                    <motion.div
                        variants={fadeIn}
                    >
                        <Footer />
                    </motion.div>
                </div>
            </div>
        </motion.div>
    )
}

export default BitEuPage
